import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  User, 
  CheckCircle, 
  AlertCircle, 
  Users, 
  MapPin,
  Phone,
  Mail,
  Award,
  Activity
} from 'lucide-react';

interface StaffAxisDemoProps {
  isActive: boolean;
}

const StaffAxisDemo: React.FC<StaffAxisDemoProps> = ({ isActive }) => {
  const [currentView, setCurrentView] = useState(0);
  const [animationStep, setAnimationStep] = useState(0);

  const staffMembers = [
    {
      id: 1,
      name: 'Dr. Sarah Smith',
      role: 'Cardiologist',
      status: 'available',
      time: '9:00 AM - 5:00 PM',
      location: 'Room 301',
      patients: 8,
      phone: '+1 (555) 123-4567',
      email: 'sarah.smith@bioaxis.com',
      specialization: 'Cardiac Surgery',
      experience: '12 years',
      avatar: '👩‍⚕️'
    },
    {
      id: 2,
      name: 'Nurse Jennifer Jones',
      role: 'Head Nurse',
      status: 'busy',
      time: '8:00 AM - 6:00 PM',
      location: 'ICU Ward',
      patients: 12,
      phone: '+1 (555) 987-6543',
      email: 'jennifer.jones@bioaxis.com',
      specialization: 'Critical Care',
      experience: '8 years',
      avatar: '👩‍⚕️'
    },
    {
      id: 3,
      name: 'Dr. Michael Wilson',
      role: 'General Practitioner',
      status: 'available',
      time: '10:00 AM - 4:00 PM',
      location: 'Room 205',
      patients: 6,
      phone: '+1 (555) 456-7890',
      email: 'michael.wilson@bioaxis.com',
      specialization: 'Family Medicine',
      experience: '15 years',
      avatar: '👨‍⚕️'
    },
    {
      id: 4,
      name: 'Tech Alex Rodriguez',
      role: 'Lab Technician',
      status: 'on_break',
      time: '7:00 AM - 3:00 PM',
      location: 'Laboratory',
      patients: 0,
      phone: '+1 (555) 321-0987',
      email: 'alex.rodriguez@bioaxis.com',
      specialization: 'Clinical Lab',
      experience: '5 years',
      avatar: '🧑‍🔬'
    }
  ];

  const views = ['schedule', 'staff', 'analytics'];
  const viewTitles = ['Staff Schedule', 'Team Directory', 'Performance Analytics'];

  useEffect(() => {
    if (isActive) {
      const viewInterval = setInterval(() => {
        setCurrentView(prev => (prev + 1) % views.length);
        setAnimationStep(0);
      }, 4000);

      const stepInterval = setInterval(() => {
        setAnimationStep(prev => prev + 1);
      }, 800);

      return () => {
        clearInterval(viewInterval);
        clearInterval(stepInterval);
      };
    }
  }, [isActive]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-400';
      case 'busy': return 'bg-red-400';
      case 'on_break': return 'bg-yellow-400';
      default: return 'bg-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return 'Available';
      case 'busy': return 'Busy';
      case 'on_break': return 'On Break';
      default: return 'Unknown';
    }
  };

  const renderScheduleView = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-primary-500" />
        <span className="font-semibold text-slate-700 dark:text-slate-300">Today's Schedule</span>
        <div className="ml-auto text-xs text-slate-500">Live Updates</div>
      </div>
      
      {staffMembers.map((staff, index) => (
        <motion.div
          key={staff.id}
          initial={{ x: -30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: index * 0.15 }}
          className="flex items-center justify-between p-3 bg-white dark:bg-slate-700 rounded-lg shadow-sm border border-slate-200 dark:border-slate-600"
        >
          <div className="flex items-center gap-3">
            <div className="text-lg">{staff.avatar}</div>
            <div>
              <div className="font-medium text-sm text-slate-800 dark:text-slate-200">{staff.name}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{staff.role}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs font-medium text-slate-600 dark:text-slate-300">{staff.time}</div>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <MapPin className="w-3 h-3" />
                {staff.location}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(staff.status)}`} />
              <span className="text-xs text-slate-600 dark:text-slate-400">{getStatusText(staff.status)}</span>
            </div>
          </div>
        </motion.div>
      ))}
      
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-700"
      >
        <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-medium">All staff scheduled for today</span>
        </div>
      </motion.div>
    </div>
  );

  const renderStaffView = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-5 h-5 text-primary-500" />
        <span className="font-semibold text-slate-700 dark:text-slate-300">Team Directory</span>
        <div className="ml-auto text-xs text-slate-500">{staffMembers.length} Members</div>
      </div>
      
      {staffMembers.slice(0, 2).map((staff, index) => (
        <motion.div
          key={staff.id}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: index * 0.2 }}
          className="p-3 bg-white dark:bg-slate-700 rounded-lg shadow-sm border border-slate-200 dark:border-slate-600"
        >
          <div className="flex items-start gap-3">
            <div className="text-xl">{staff.avatar}</div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm text-slate-800 dark:text-slate-200">{staff.name}</span>
                <div className={`w-2 h-2 rounded-full ${getStatusColor(staff.status)}`} />
              </div>
              
              <div className="text-xs text-slate-600 dark:text-slate-400 mb-2">{staff.role}</div>
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1">
                  <Award className="w-3 h-3 text-primary-400" />
                  <span>{staff.specialization}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Activity className="w-3 h-3 text-green-400" />
                  <span>{staff.experience}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Phone className="w-3 h-3 text-blue-400" />
                  <span>{staff.phone.slice(-4)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Mail className="w-3 h-3 text-purple-400" />
                  <span>Email</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  const renderAnalyticsView = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-5 h-5 text-primary-500" />
        <span className="font-semibold text-slate-700 dark:text-slate-300">Performance Analytics</span>
        <div className="ml-auto text-xs text-slate-500">Real-time</div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700"
        >
          <div className="text-center">
            <div className="text-lg font-bold text-green-600 dark:text-green-400">94%</div>
            <div className="text-xs text-green-600 dark:text-green-400">Staff Utilization</div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700"
        >
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">26</div>
            <div className="text-xs text-blue-600 dark:text-blue-400">Patients Today</div>
          </div>
        </motion.div>
      </div>
      
      <motion.div
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600"
      >
        <div className="text-xs font-medium mb-2 text-slate-700 dark:text-slate-300">Shift Coverage</div>
        <div className="space-y-1">
          {['Morning', 'Afternoon', 'Evening'].map((shift, index) => (
            <div key={shift} className="flex items-center justify-between">
              <span className="text-xs text-slate-600 dark:text-slate-400">{shift}</span>
              <div className="flex items-center gap-1">
                <div className="w-16 bg-slate-200 dark:bg-slate-600 rounded-full h-1">
                  <motion.div
                    className="bg-primary-500 h-1 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${85 + index * 5}%` }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
                  />
                </div>
                <span className="text-xs text-slate-500">{85 + index * 5}%</span>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  const renderCurrentView = () => {
    switch (views[currentView]) {
      case 'schedule': return renderScheduleView();
      case 'staff': return renderStaffView();
      case 'analytics': return renderAnalyticsView();
      default: return renderScheduleView();
    }
  };

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full h-80 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-700 rounded-xl p-6 shadow-xl border border-slate-200 dark:border-slate-600"
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">StaffAxis</h3>
              </div>
              <div className="flex gap-1">
                {views.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentView ? 'bg-primary-500' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* View Title */}
            <motion.div
              key={currentView}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4"
            >
              <h4 className="text-md font-semibold text-slate-700 dark:text-slate-300">
                {viewTitles[currentView]}
              </h4>
            </motion.div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              <motion.div
                key={currentView}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderCurrentView()}
              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StaffAxisDemo;